import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../config/api';

interface Locker {
  id: string;
  locker_number: string;
  status: 'available' | 'occupied' | 'maintenance' | 'reserved';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  locations: {
    id: string;
    name: string;
    address: string;
    city: string;
  };
  locker_sizes: {
    id: string;
    name: string;
    width_mm: number;
    height_mm: number;
    depth_mm: number;
    hourly_rate: number;
    daily_rate: number;
  };
}

interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
}

interface LockerSize {
  id: string;
  name: string;
  width_mm: number;
  height_mm: number;
  depth_mm: number;
}

const AdminLockerManagement: React.FC = () => {
  const { user } = useAuth();
  const [lockers, setLockers] = useState<Locker[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [lockerSizes, setLockerSizes] = useState<LockerSize[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLocker, setEditingLocker] = useState<Locker | null>(null);

  // Form state for adding/editing lockers
  const [formData, setFormData] = useState({
    locationId: '',
    sizeId: '',
    lockerNumber: '',
    status: 'available' as 'available' | 'occupied' | 'maintenance' | 'reserved',
    isActive: true
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [lockersRes, locationsRes, sizesRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE}/api/lockers`),
        fetch(`${API_ENDPOINTS.BASE}/api/locations`),
        fetch(`${API_ENDPOINTS.BASE}/api/lockers/sizes`)
      ]);

      if (lockersRes.ok) {
        const lockersData = await lockersRes.json();
        setLockers(lockersData.lockers || []);
      }

      if (locationsRes.ok) {
        const locationsData = await locationsRes.json();
        setLocations(locationsData.locations || []);
      }

      if (sizesRes.ok) {
        const sizesData = await sizesRes.json();
        setLockerSizes(sizesData.sizes || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const url = editingLocker
        ? `${API_ENDPOINTS.BASE}/api/lockers/${editingLocker.id}`
        : `${API_ENDPOINTS.BASE}/api/lockers`;

      const method = editingLocker ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          location_id: formData.locationId,
          size_id: formData.sizeId,
          locker_number: formData.lockerNumber,
          status: formData.status,
          is_active: formData.isActive
        })
      });

      if (response.ok) {
        toast.success(editingLocker ? 'Locker updated successfully!' : 'Locker added successfully!');
        setShowAddForm(false);
        setEditingLocker(null);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving locker:', error);
      toast.error('Failed to save locker');
    }
  };

  const handleEdit = (locker: Locker) => {
    setEditingLocker(locker);
    setFormData({
      locationId: locker.locations.id,
      sizeId: locker.locker_sizes.id,
      lockerNumber: locker.locker_number,
      status: locker.status,
      isActive: locker.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (lockerId: string) => {
    if (!confirm('Are you sure you want to delete this locker?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/lockers/${lockerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Locker deleted successfully!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to delete locker');
      }
    } catch (error) {
      console.error('Error deleting locker:', error);
      toast.error('Failed to delete locker');
    }
  };

  const resetForm = () => {
    setFormData({
      locationId: '',
      sizeId: '',
      lockerNumber: '',
      status: 'available' as 'available' | 'occupied' | 'maintenance' | 'reserved',
      isActive: true
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'occupied': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'reserved': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lockers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Locker Management</h1>
              <p className="text-gray-600 mt-2">Manage all lockers across locations</p>
            </div>
            <button
              onClick={() => {
                setShowAddForm(true);
                setEditingLocker(null);
                resetForm();
              }}
              className="btn-primary"
            >
              Add New Locker
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingLocker ? 'Edit Locker' : 'Add New Locker'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <select
                    value={formData.locationId}
                    onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Location</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name} - {location.city}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Locker Size
                  </label>
                  <select
                    value={formData.sizeId}
                    onChange={(e) => setFormData({ ...formData, sizeId: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Size</option>
                    {lockerSizes.map((size) => (
                      <option key={size.id} value={size.id}>
                        {size.name} ({size.width_mm/10}×{size.height_mm/10}×{size.depth_mm/10}cm)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Locker Number
                  </label>
                  <input
                    type="text"
                    value={formData.lockerNumber}
                    onChange={(e) => setFormData({ ...formData, lockerNumber: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., GC-S01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="available">Available</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="reserved">Reserved</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" className="btn-primary">
                  {editingLocker ? 'Update Locker' : 'Add Locker'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingLocker(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lockers List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              All Lockers ({lockers.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lockers.map((locker) => (
                  <tr key={locker.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {locker.locker_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{locker.locations.name}</div>
                      <div className="text-sm text-gray-500">{locker.locations.city}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{locker.locker_sizes.name}</div>
                      <div className="text-sm text-gray-500">
                        {locker.locker_sizes.width_mm/10}×{locker.locker_sizes.height_mm/10}×{locker.locker_sizes.depth_mm/10}cm
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(locker.status)}`}>
                        {locker.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${locker.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {locker.is_active ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(locker)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(locker.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLockerManagement;
